<template>
  <q-dialog
    ref="dialog"
    @hide="onDialogHide"
  >
    <q-carousel
      ref="carousel"
      v-model="docSlider"
      v-model:fullscreen="fullScreen"
      animated
      arrows
      :infinite="allFiles.length > 1"
      control-color="black"
      class="slide-container"
      :class="{ 'container-height': !fullScreen }"
    >
      <q-carousel-slide
        v-for="fileData in allFiles"
        :key="fileData.fileId"
        :name="fileData.fileId"
        class="no-padding column no-wrap full-height"
      >
        <q-card
          class="preview-file-card no-padding"
        >
          <q-card-section class="row">
            <div class="text-h6 ellipsis col">
              {{ fileData.fileName }}
            </div>
            <q-btn
              v-if="isMediaFileOrPdf(fileData.fileType)"
              flat
              round
              dense
              icon="cloud_download"
              color="primary"
              :disable="offlineMode"
              @click="onDownloadFile(fileData)"
            />
            <q-btn
              flat
              round
              dense
              icon="close"
              class="z-top"
              @click="hide"
            />
          </q-card-section>
          <q-card-section
            :class="{
              'full-height': !fullScreen,
              'min-document-height': fullScreen
            }"
            class="q-pt-none document-wrap"
          >
            <q-btn
              v-if="showFullScreenToggler(fileData.fileType)"
              dense
              rounded
              size="md"
              color="white"
              text-color="black"
              :icon="fullScreen ? 'fullscreen_exit' : 'fullscreen'"
              class="absolute-top-right z-top"
              @click="setDocumentToFullscreen(fileData.fileType, fileUrl)"
            />
            <div
              v-if="fileData.fileType.includes('image/')"
            >
              <q-card flat>
                <q-img
                  ref="image"
                  :src="fileUrl"
                  no-spinner
                  class="document-container-size img-cover"
                  @click="setDocumentToFullscreen(fileData.fileType, fileUrl)"
                />
              </q-card>
            </div>
            <div
              v-else-if="fileData.fileType.includes('video/')"
              class="relative-position"
            >
              <video
                :src="fileUrl"
                class="document-container-size"
                style="max-height: 85vh"
                controls
              />
            </div>
            <div v-else-if="fileData.fileType === 'application/pdf'">
              <q-card
                flat
                class="q-mb-sm"
              >
                <pdf-viewer
                  v-model:current-page="currentPage"
                  :pdf-src="fileUrl"
                  @fullscreen="setDocumentToFullscreen(fileData.fileType, fileUrl, currentPage)"
                />
              </q-card>
            </div>
            <banner
              v-else
              :message="$t('common.modals.previewFile.previewIsNotAvailable')"
            >
              <template #action>
                <div class="text-right">
                  <q-btn
                    flat
                    @click="onDownloadEmit"
                  >
                    {{ $t('clientFiles.listItemTextDownload') }}
                  </q-btn>
                </div>
              </template>
            </banner>
          </q-card-section>
        </q-card>
      </q-carousel-slide>
    </q-carousel>
  </q-dialog>
</template>

<script>
import Banner from 'components/UI/Banner';
import FullscreenDocument from 'components/UI/modals/FullscreenDocument';
import PdfViewer from 'components/UI/PdfViewer.vue';
import { find } from 'lodash';
import { mapState } from 'pinia';
import Interactions from 'src/mixins/Interactions';
import { network } from 'src/store/network';

export default {
  name: 'PreviewFile',

  components: {
    PdfViewer,
    Banner,
  },

  mixins: [Interactions],

  props: {
    allFiles: {
      type: Array,
      required: true,
    },
    currentFileId: {
      type: String,
      required: true,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      docSlider: null,
      fileUrl: null,
      currentPage: 1,
      onOverlay: false,
    };
  },

  computed: {
    ...mapState(network, ['offlineMode']),
    fullScreen: {
      get() {
        return this.$q.platform.is.mobile;
      },
      set(newValue) {
        return newValue;
      },
    }, // fullScreen()
  },

  watch: {
    docSlider: {
      handler(nextSlide) {
        this.currentPage = 1;
        this.setCurrentSlide(nextSlide);
      },
    },
  },

  methods: {
    setDocumentToFullscreen(fileType, fileUrl) {
      this.openComponentDialog(FullscreenDocument, {
        fileType,
        fileUrl,
        pdfPage: this.currentPage,
      });
    }, // setDocumentToFullscreen()

    isMediaFileOrPdf(fileType) {
      return fileType.includes('image/') || fileType.includes('video/') || fileType === 'application/pdf';
    }, // isMediaFileOrPdf()

    onDownloadFile() {
      return window.open(this.fileUrl);
    }, // onDownloadFile()

    showFullScreenToggler(fileType) {
      return (fileType.includes('image/') || fileType === 'application/pdf') && !this.$q.platform.is.mobile;
    }, // showFullScreenToggler()

    async setCurrentSlide(fileId) {
      try {
        this.$q.loading.show({ delay: 0 });
        this.fileUrl = find(this.allFiles, { fileId }).url;
      } finally {
        this.$q.loading.hide();
      }
    }, // setCurrentSlide()

    show() {
      this.docSlider = this.currentFileId;
      this.setCurrentSlide(this.currentFileId);
      this.$refs.dialog.show();
    }, // show()

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    }, // onDialogHide()

    onDownloadEmit() {
      this.$emit('ok', { download: true, downloadFileId: this.docSlider });
      this.hide();
    }, // onDownloadEmit()
  },
};
</script>

<style lang="scss" scoped>
  .slide-container {
    background-color: rgba(0, 0, 0, .3);
    min-width: 60vw;
    height: auto;
    :deep(.q-carousel__control) {
      top: 30px;
      bottom: 0;
    }
    :deep(.q-carousel__control button) {
      height: 85%;
      border-radius: 0;
    }
  }
  .container-height {
    max-height: calc(100vh - 36px);
  }
  .document-wrap {
    width: 90%;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    > :last-child {
      display: block;
      width: 100%;
      height: 100%;
    }
  }
  .min-document-height {
    min-height: calc(90vh - 10px);

    @media only screen and (min-width: $breakpoint-md) {
      min-height: calc(94vh - 8px);
    }
    @media only screen and (max-width: $breakpoint-sm) {
      min-height: calc(91vh - 6px);
      padding-right: 20px;
      padding-left: 20px;
    }
  }
  .document-container-size {
    width: 100%;
    height: 100%;
  }
  .img-cover {
    max-height: 79vh;
    :deep(.q-img__image) {
      background-size: contain !important;
    }
  }
  .preview-file-card {
    width: 100%;
    height: auto;
  }
</style>
